import type React from 'react';
import {useState} from 'react';
import {Button, Stack, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {ExternalSignals} from '../../types/signalsTypes';
import {ProfileSection} from '../components/ProfileSection';
import {MappingDialog} from './MappingDialog';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});

type Props = {
    category: string;
    data: ExternalSignals;
    onConfirm: (node: string, code: string) => Promise<unknown>;
};

/** Taxonomy mapping (spec 4.12): the category's node in the provider taxonomy, confirmed by the team. */
export const TaxonomyMappingCard: React.FC<Props> = ({category, data, onConfirm}) => {
    const [open, setOpen] = useState(false);
    const m = data.mapping;
    const match = m.score >= 80 ? 'success' : m.score >= 50 ? 'warning' : 'error';
    return (
        <ProfileSection title="Taxonomy mapping"
                        actions={data.canEdit && <Button size="small" onClick={() => setOpen(true)}>Review mapping</Button>}>
            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={{sm: 'center'}}>
                <Stack sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="body2">
                        <b>{category}</b>{' → '}{m.node ? <b>{m.node}</b> : <i>{`suggested: ${m.suggestion}`}</i>}
                        {m.code && <Typography component="span" variant="body2" color="text.secondary">{` · ${m.code}`}</Typography>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {m.node && m.confirmedAt ? `Confirmed by ${m.confirmedBy ?? 'unknown'}, ${WHEN.format(new Date(m.confirmedAt))}`
                            : 'Not confirmed yet: signals are routed by the suggested node.'}
                    </Typography>
                </Stack>
                {m.node && <Tag label={`Match ${m.score}`} color={match} solid={false} title="How closely the node name matches the category"/>}
            </Stack>
            {open && <MappingDialog category={category} mapping={m} onConfirm={onConfirm} onClose={() => setOpen(false)}/>}
        </ProfileSection>
    );
};
